import { Download, Terminal, Cpu, Package, Puzzle, EyeOff, Mic, Video, Network, Shield, Apple, Monitor } from "lucide-react";
import { API } from "@/lib/api";
import BackButton from "@/components/BackButton";

const DETECTS = [
  { icon: Cpu, t: "Running processes", d: "Parakeet AI, Cluely, Interview Coder, Final Round AI, LockedIn AI, Sensei, remote-control apps" },
  { icon: Package, t: "Installed programs", d: "Windows registry, macOS /Applications, Linux desktop entries" },
  { icon: Puzzle, t: "Browser extensions", d: "Chrome / Edge / Brave AI answer & sidebar extensions" },
  { icon: EyeOff, t: "Hidden overlay windows", d: "Transparent, always-on-top, click-through & screen-capture-excluded windows" },
  { icon: Mic, t: "Virtual audio devices", d: "VB-CABLE, VoiceMeeter, BlackHole, Soundflower, Loopback" },
  { icon: Video, t: "Virtual camera devices", d: "OBS Virtual Camera, ManyCam, Snap Camera, XSplit VCam" },
  { icon: Network, t: "Network connections", d: "Live sockets owned by a cheating tool or reaching known AI servers (parakeet.ai, cluely.com…) — flagged harder" },
];

function Cmd({ children }) {
  return <code className="term block px-4 py-3 text-slate-300 text-xs whitespace-pre-wrap">{children}</code>;
}

export default function Downloads() {
  return (
    <div className="fade-up">
      <BackButton />
      <div className="mb-8">
        <p className="eyebrow mb-2">ONE-TIME OFFLINE SCANNER</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Scanner Agent</h1>
        <p className="text-slate-400 text-sm mt-3 max-w-3xl leading-relaxed">
          The scanner is a <span className="text-slate-200">fully offline, rule-based</span> detector generated per
          candidate as a personalized Python file tied to a single one-time token. Package it into a native
          Windows <span className="font-mono">.exe</span> or macOS <span className="font-mono">.app</span> with
          PyInstaller. Detection runs 100% locally; it only reaches the network to validate its token and upload the
          report, then self-destructs.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {DETECTS.map((x) => (
          <div key={x.t} className="card-soc p-5">
            <x.icon className="w-5 h-5 text-cyan-400 mb-3" />
            <div className="font-head font-semibold text-sm mb-1">{x.t}</div>
            <div className="text-xs text-slate-500 leading-relaxed">{x.d}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-soc p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <Download className="w-5 h-5 text-cyan-400" />
            <h2 className="font-head font-bold text-lg">1 · Get the scanner</h2>
          </div>
          <p className="text-sm text-slate-400 mb-3">
            Generate a token (Dashboard → Generate Scan Token). The candidate can run the scanner with a single
            command — no Python required on Windows (a portable runtime is fetched automatically):
          </p>
          <Cmd>{`Windows PowerShell:\nirm "${API}/agent/install.ps1?token=<TOKEN>" | iex\n\nmacOS Terminal:\ncurl -fsSL "${API}/agent/install.sh?token=<TOKEN>" | bash`}</Cmd>
          <p className="text-sm text-slate-400 mt-4 mb-3">Or download the personalized script to package it yourself:</p>
          <Cmd>{`GET ${API}/agent/download?token=<YOUR_TOKEN>`}</Cmd>
          <p className="text-sm text-slate-400 mt-4 mb-3">Build dependencies on a machine matching the target OS:</p>
          <Cmd>pip install psutil pyinstaller</Cmd>
        </div>

        <div className="card-soc p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <Terminal className="w-5 h-5 text-cyan-400" />
            <h2 className="font-head font-bold text-lg">2 · Build the executable</h2>
          </div>
          <div className="flex items-center gap-2 mb-2 text-slate-300 text-sm">
            <Monitor className="w-4 h-4" /> Windows (.exe) — run on Windows
          </div>
          <Cmd>{`pyinstaller --onefile --console \\
  --name sentinel_scanner sentinel_scanner.py
# -> dist\\sentinel_scanner.exe`}</Cmd>
          <div className="flex items-center gap-2 mt-4 mb-2 text-slate-300 text-sm">
            <Apple className="w-4 h-4" /> macOS (.app) — run on macOS
          </div>
          <Cmd>{`pyinstaller --onefile --console \\
  --name sentinel_scanner sentinel_scanner.py
# -> dist/sentinel_scanner`}</Cmd>
          <p className="text-xs text-slate-500 mt-4 flex items-start gap-2">
            <Shield className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
            PyInstaller is not a cross-compiler — build the Windows exe on Windows and the macOS build on macOS.
          </p>
        </div>
      </div>

      <div className="card-soc p-6 mt-5">
        <div className="flex items-center gap-2.5 mb-3">
          <Shield className="w-5 h-5 text-emerald-400" />
          <h2 className="font-head font-bold text-lg">3 · Run &amp; one-time self-destruct</h2>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          The candidate runs the executable once. It validates the token (rejecting it if expired or already used),
          performs the offline scan, uploads the encrypted report to this console, prints the verdict, and — when
          self-destruct is enabled — deletes itself. The token is then permanently invalidated. The candidate's
          scan page (<span className="text-emerald-300">Candidate Page</span> link on any token) also runs a real
          browser-level virtual mic/camera check and auto-refreshes when the scanner result arrives.
        </p>
      </div>
    </div>
  );
}
